/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { FiX } from 'react-icons/fi'

import { FilterSelect } from '@/pages/audit-logs/FilterSelect'

import { type GeofenceCategory, LOW_FUEL_CATEGORY_TYPE_ID } from '../types'

interface CategoryMultiSelectProps {
  categories: GeofenceCategory[]
  selected: number[]
  onChange: (ids: number[]) => void
}

/**
 * The geofence's categories: the chosen ones as removable chips, a searchable checklist to add
 * more.
 */
export const CategoryMultiSelect = ({ categories, selected, onChange }: CategoryMultiSelectProps) => {
  const chosen = selected.map((id) => categories.find((c) => c.id === id)).filter((c): c is GeofenceCategory => !!c)

  return (
    <div className="flex flex-col gap-2">
      {chosen.length > 0 ? (
        <ul className="flex flex-wrap gap-1" aria-label="Chosen categories">
          {chosen.map((category) => (
            <li
              key={category.id}
              className={`badge gap-1 ${category.typeId === LOW_FUEL_CATEGORY_TYPE_ID ? 'badge-warning' : 'badge-outline'}`}
            >
              {category.name}
              <button
                type="button"
                className="cursor-pointer opacity-60 hover:opacity-100"
                aria-label={`Remove ${category.name}`}
                onClick={() => onChange(selected.filter((id) => id !== category.id))}
              >
                <FiX className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-base-content/50">Not categorised</p>
      )}
      <div className="flex items-center gap-2">
        <FilterSelect
          label="Choose"
          options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
          selected={selected.map(String)}
          onChange={(next) => onChange(next.map(Number))}
          searchable
          emptyText="This company has no categories yet."
        />
      </div>
    </div>
  )
}
